import React, { useEffect, useRef, useState } from "react";

import { toast } from "react-hot-toast";

import { motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  selectEmail,
  selectVerifyType,
} from "../../../states/slices/globalReducer";
import { VerifyTokenTypes } from "../../../types";
import { resendtoken, verifytoken } from "../../../Services/auth";
import { setToken } from "../../../states/slices/authReducer";
import LinkHead from "../../../shared/components/LinkHead";
import AuthLayout from "../../../shared/Layouts/AuthLayout";
import { GO_BACK, WARN } from "../../../assets";
import ButtonV2 from "../../../shared/components/buttonV2";

function VerifiOtp() {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState({
    verifying: false,
    resending: false,
  });
  const dispatch = useAppDispatch();
  const verifyType = useAppSelector(selectVerifyType);
  const email = useAppSelector(selectEmail);

  const inputRefs = useRef<HTMLInputElement | null[]>([]);
  const shakeAnimation = error ? "animate-shake" : "";
  const handleChange = (
    element: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (isNaN(Number(element.target.value))) return;

    const newOtp = [...otp];
    newOtp[index] = element.target.value;
    setOtp(newOtp);

    if (element.target.value && index < otp.length - 1) {
      // @ts-ignore
      inputRefs.current[index + 1]?.focus();
    }
    // if (newOtp.every((digit) => digit !== "")) {
    //   setTimeout(() => handleVerifyOtp(), 300);
    // }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && otp[index] === "") {
      if (index > 0) {
        // @ts-ignore
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePasteToken = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (!pastedData) return;

    // spread the pasted characters to the otp array

    const newOtp = [...otp];

    for (let i = 0; i < pastedData.length && index + i < otp.length; i++) {
      newOtp[index + i] = pastedData[i];
    }

    setOtp(newOtp);

    const nexIndex = Math.min(index + pastedData.length, otp.length - 1);
    // @ts-ignore
    inputRefs?.current[nexIndex]?.focus();
  };

  const handleVerifyOtp = async () => {
    setLoading({ ...loading, verifying: true });
    const payload: VerifyTokenTypes = {
      token: otp.join(""),
      type: verifyType,
    };
    try {
      const response = await verifytoken(payload);
      if (response) {
        toast.success(response?.message);
        if (verifyType === "REGISTER") {
          window.history.replaceState({}, "", "/sign-in");
        } else if (verifyType === "PASSWORD_RESET") {
          window.history.replaceState({}, "", "/reset-password");
          dispatch(setToken(response?.data?.token));
        }

        setTimeout(() => {
          window.location.reload();
        }, 1000);
        setSuccess(true);
      }
    } catch (error: any) {
      //   toast.error(error?.response?.data?.message);
      setError(true);
    } finally {
      setLoading({ ...loading, verifying: false });
    }
  };

  useEffect(() => {
    if (error) {
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => setError(false), 2000);
    }
  }, [error]);

  const handleResendOtp = async () => {
    setError(false);
    setLoading({ ...loading, resending: true });
    const payload: VerifyTokenTypes = {
      type: "REGISTER",
      email: email,
    };
    try {
      const response = await resendtoken(payload);
      if (response) {
        toast.success(response?.message);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading({ ...loading, resending: false });
    }
  };
  return (
    <>
      <LinkHead title="Email verification" content="" />

      <AuthLayout>
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="lg:w-[40rem] w-full flex flex-col gap-y-4 bg-white shadow-blur rounded-xl p-10"
        >
          <button
            onClick={() => window.history.back()}
            className="flex transition-all hover:scale-95 duration-200 items-center rounded-lg justify-center gap-x-2 border border-borderColor w-[103px] h-[40px] shadow-box"
          >
            <img src={GO_BACK} className="w-[20px] h-[20px]" alt="" />
            <span className="text-[16px] font-medium text-primary-300">
              Back
            </span>
          </button>

          <span className="text-2xl font-semibold text-black">
            Verify Email Address
          </span>
          <span className="text-primary-50 font-normal text-sm">
            We sent a 6-digit code to {email.slice(0, 1)}
            ***********************@{email.split("@")[1]}, please enter the code
            below.
          </span>

          <div className="mt-6 flex space-x-3 lg:flex-row flex-wrap justify-center gap-y-2">
            {otp.map((_, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                value={otp[index]}
                onPaste={(e) => handlePasteToken(e, index)}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                // @ts-ignore
                ref={(ref) => (inputRefs.current[index] = ref)}
                className={`lg:h-[60px] lg:w-[60px] w-[40px] h-[40px] rounded-xl border-2 ${
                  error
                    ? "border-[#FF3B2D] bg-[#FFE6E4]"
                    : success && "border-green-600 bg-green-200"
                } ${shakeAnimation} border-borderColor text-center text-xl focus:border-globalGold focus:shadow-custom focus:outline-none`}
              />
            ))}
          </div>
          {error && (
            <div className="flex items-center gap-x-2 justify-center">
              <img src={WARN} className="w-[13.33px] h-[12.67px]" alt="" />
              <span className="text-xs font-medium text-[#FF3B2D]">
                Invalid OTP, please try again
              </span>
            </div>
          )}

          <div className="flex items-center justify-center w-full mt-4">
            <span className="text-sm font-medium text-primary-50">
              Didn't receive any code?{" "}
              <span
                className="text-globalGold cursor-pointer"
                onClick={handleResendOtp}
              >
                {loading.resending ? "please wait..." : "Resend OTP"}
              </span>{" "}
            </span>
          </div>
          <ButtonV2
            btnStyle="w-full bg-globalGold h-[48px] rounded-xl flex items-center justify-center"
            textStyle="text-white font-semibold text-[16px]"
            title="Verify"
            loaderColor="#ffffff"
            loading={loading.verifying}
            disabled={loading.verifying || otp.includes("")}
            handleClick={handleVerifyOtp}
          />
        </motion.div>
      </AuthLayout>
    </>
  );
}

export default VerifiOtp;
